/**
 * CX Tabs: accessible tablist/tabpanel. Event delegation, minimal DOM writes.
 *
 * Theme Editor performance: In design mode, only the default-active tab panel
 * has real content (Liquid renders placeholders for others). So sliders/carousels
 * inside tabs are only in the active panel and we do not need to skip init for
 * hidden panels—they have no heavy content to initialise.
 */
(function () {
  const SEL_CONTAINER = '[data-tabs]';
  const SEL_PANEL = '[data-tabs-panel]';

  function getContainers(root) {
    const r = root || document;
    if (!r.querySelectorAll) return [];
    const list = Array.from(r.querySelectorAll(SEL_CONTAINER));
    if (r instanceof Element && r.matches(SEL_CONTAINER) && !list.includes(r)) list.unshift(r);
    return list;
  }

  function setActive(container, tabs, panels, index, focusTab) {
    const n = tabs.length;
    for (let i = 0; i < n; i++) {
      const on = i === index;
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[i].toggleAttribute('data-active', on);
      tabs[i].tabIndex = on ? 0 : -1;
      panels[i].toggleAttribute('hidden', !on);
      panels[i].toggleAttribute('data-active', on);
    }
    container.dataset.activeIndex = String(index);
    if (focusTab) tabs[index]?.focus();
  }

  function syncTablistScrollState(container, tablist, scrollHost) {
    if (!(tablist instanceof HTMLElement) || !(scrollHost instanceof HTMLElement)) return;

    const overflow = tablist.scrollWidth > scrollHost.clientWidth + 1;
    tablist.classList.toggle('tabs__tablist--scrollable', overflow);
    if (overflow) scrollHost.scrollLeft = 0;
  }

  function enhance(container) {
    if (!(container instanceof HTMLElement)) return;
    if (container.classList.contains('tabs--enhanced') || container.querySelector('.tabs__tablist')) return;

    const panels = Array.from(container.querySelectorAll(SEL_PANEL)).filter((p) => p instanceof HTMLElement);
    if (!panels.length) return;

    const host = container.querySelector('[data-tabs-tablist-host]') || container;
    const tablist = document.createElement('div');
    tablist.className = 'tabs__tablist';
    tablist.setAttribute('role', 'tablist');

    const fragment = document.createDocumentFragment();
    const tabs = [];
    for (let i = 0; i < panels.length; i++) {
      const panel = panels[i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tabs__tab';
      btn.setAttribute('role', 'tab');
      btn.tabIndex = -1;
      const tabId = panel.getAttribute('data-tabs-tab-id');
      const panelId = panel.getAttribute('id');
      if (tabId) btn.id = tabId;
      if (panelId) btn.setAttribute('aria-controls', panelId);
      btn.textContent = panel.getAttribute('data-tabs-title') || 'Tab';
      fragment.appendChild(btn);
      tabs.push(btn);

      panel.setAttribute('role', 'tabpanel');
      if (tabId) panel.setAttribute('aria-labelledby', tabId);
    }
    tablist.appendChild(fragment);

    if (host !== container) host.replaceChildren(tablist);
    else host.prepend(tablist);
    container.classList.add('tabs--enhanced');

    const scrollHost = container.querySelector('[data-tabs-tablist-host]');
    if (scrollHost instanceof HTMLElement) {
      scrollHost.scrollLeft = 0;
    }
    syncTablistScrollState(container, tablist, scrollHost);

    // Exactly one tab should be default; fallback to first (index 0) if none marked
    const defaultIdx = Math.max(0, panels.findIndex((p) => p.getAttribute('data-tabs-default') === 'true'));
    setActive(container, tabs, panels, defaultIdx, false);

    tablist.addEventListener('click', (e) => {
      const tab = e.target.closest?.('button[role="tab"]');
      if (!tab) return;
      const i = tabs.indexOf(tab);
      if (i >= 0) setActive(container, tabs, panels, i, false);
    });
    tablist.addEventListener('keydown', (e) => {
      const tab = e.target.closest?.('button[role="tab"]');
      if (!tab) return;
      if (tabs.indexOf(tab) < 0) return;
      const current = Number(container.dataset.activeIndex || 0);
      const last = tabs.length - 1;
      let next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = current === last ? 0 : current + 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = current === 0 ? last : current - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      if (next === null) return;
      e.preventDefault();
      setActive(container, tabs, panels, next, true);
    });
  }

  function init(root) {
    getContainers(root).forEach((el) => enhance(el));
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      getContainers(document).forEach((container) => {
        const tablist = container.querySelector('.tabs__tablist');
        const scrollHost = container.querySelector('[data-tabs-tablist-host]');
        syncTablistScrollState(container, tablist, scrollHost);
      });
    }, 150);
  });

  window.CX = window.CX || {};
  window.CX.initTabs = init;

  const run = (r) => init(r || document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document), { once: true });
  else setTimeout(() => run(document), 0);
  document.addEventListener('shopify:section:load', (e) => run(e?.target instanceof Element ? e.target : document));
  document.addEventListener('shopify:section:reorder', () => run(document));
  document.addEventListener('shopify:block:select', () => run(document));
})();
